<template>
  <div class="admin-dashboard">
    <div class="page-header">
      <div class="page-header-main">
        <div class="page-header-icon" aria-hidden="true"><v-icon size="23" color="#fff">mdi-view-dashboard-outline</v-icon></div>
        <div class="page-header-text">
          <div class="page-eyebrow">{{ greeting }} · pulpit właściciela</div>
          <h1 class="page-title">{{ $t('nav.dashboard') }}</h1>
          <p class="page-subtitle">Finanse, wypłaty i sprawy wymagające uwagi — bez przekopywania się przez raporty.</p>
        </div>
      </div>
      <div class="page-header-actions">
        <div class="dashboard-health" :class="`dashboard-health--${sourceHealthTone}`">
          <span class="dashboard-health__dot" />
          <div><small>Stan danych</small><strong>{{ sourceShortStatus }}</strong></div>
        </div>
        <button
          class="action-pill"
          :class="editMode ? 'action-pill--primary' : 'action-pill--neutral'"
          @click="toggleEdit"
        >
          <v-icon size="14">{{ editMode ? 'mdi-content-save' : 'mdi-pencil' }}</v-icon>
          {{ editMode ? $t('dashboard.editDone') : $t('dashboard.edit') }}
        </button>
        <button v-if="editMode" class="action-pill action-pill--error" @click="resetLayout">
          <v-icon size="14">mdi-restore</v-icon>{{ $t('dashboard.editReset') }}
        </button>
      </div>
    </div>

    <!-- Status synchronizacji Bolt -->
    <div
      v-if="boltStatusBanner"
      class="bolt-banner mb-3"
      :class="boltStatusBanner.type === 'error' ? 'bolt-banner--error' : 'bolt-banner--warning'"
    >
      <v-icon size="20" :color="boltStatusBanner.type === 'error' ? '#FF8A80' : '#FFB74D'">
        {{ boltStatusBanner.type === 'error' ? 'mdi-alert-octagon' : 'mdi-clock-alert-outline' }}
      </v-icon>
      <div class="flex-grow-1">
        <div class="bolt-banner-title">{{ boltStatusBanner.title }}</div>
        <div class="bolt-banner-msg">{{ boltStatusBanner.message }}</div>
      </div>
      <router-link to="/bolt-sync-drivers" class="action-pill action-pill--primary" style="text-decoration: none;">
        <v-icon size="14">mdi-cloud-sync</v-icon>Sprawdź synchronizację
      </router-link>
    </div>

    <!-- Zakres, źródło i filtry w jednym centrum sterowania -->
    <section class="dashboard-control mb-3">
      <div class="dashboard-control-top">
        <div class="dash-period-block">
          <div class="dash-control-icon"><v-icon size="20">mdi-calendar-range</v-icon></div>
          <div>
            <div class="dash-hero-label">Aktualny zakres</div>
            <div class="dash-hero-range">{{ formatRange(appliedFilters.from, appliedFilters.to) }}</div>
          </div>
        </div>
        <div class="dash-source-summary" :class="'source-' + ((sourceStatus && sourceStatus.overallSource) || 'api')">
          <div class="dash-source-icon"><v-icon size="18" :color="sourceIconColor">{{ sourceIcon }}</v-icon></div>
          <div><small>Źródło danych</small><strong>{{ sourceSummary }}</strong></div>
        </div>
      </div>
      <div class="dash-hero-presets">
        <button
          v-for="p in presetButtons"
          :key="p.value"
          class="hero-preset"
          :class="{ 'hero-preset--active': activePreset === p.value }"
          @click="activePreset = p.value; onPresetChange()"
        >{{ p.text }}</button>
      </div>

      <div class="dashboard-filter-row">
        <div class="dashboard-filter-label"><v-icon size="17">mdi-filter-variant</v-icon><span>Widok danych</span></div>
        <v-select
          v-model="filters.subCompanyIds"
          :items="subCompanies"
          item-text="name"
          item-value="id"
          label="Oddziały"
          placeholder="Wszystkie oddziały"
          outlined dense hide-details
          multiple chips small-chips clearable
          prepend-inner-icon="mdi-domain"
          class="dashboard-branch-filter"
          @change="applyFilters"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index === 0" x-small>{{ item.name }}</v-chip>
            <span v-if="index === 1" class="caption dash-more-selection">
              +{{ filters.subCompanyIds.length - 1 }}
            </span>
          </template>
        </v-select>
        <v-text-field
          v-if="activePreset === 'custom'"
          v-model="filters.from"
          :label="$t('common.dateFrom')"
          type="date"
          outlined dense hide-details
          prepend-inner-icon="mdi-calendar-start"
          class="dashboard-date-filter"
        />
        <v-text-field
          v-if="activePreset === 'custom'"
          v-model="filters.to"
          :label="$t('common.dateTo')"
          type="date"
          outlined dense hide-details
          prepend-inner-icon="mdi-calendar-end"
          class="dashboard-date-filter"
        />
        <button class="action-pill action-pill--neutral dashboard-refresh" @click="applyFilters">
          <v-icon size="14">mdi-refresh</v-icon>{{ $t('common.refresh') }}
        </button>
      </div>

      <details v-if="sourceStatus && sourceStatus.weeks && sourceStatus.weeks.length" class="dashboard-source-details">
        <summary>
          <span class="dashboard-source-row-label"><v-icon size="16">mdi-database-check</v-icon>Jakość danych</span>
          <span>{{ sourceStatus.weeks.length }} {{ sourceStatus.weeks.length === 1 ? 'tydzień' : 'tygodni' }} w wybranym zakresie</span>
          <v-icon size="17" class="dashboard-source-chevron">mdi-chevron-down</v-icon>
        </summary>
        <div class="dash-source-strip">
          <span
            v-for="w in sourceStatus.weeks"
            :key="w.weekKey"
            class="src-badge"
            :class="w.source === 'csv' ? 'src-badge--csv' : 'src-badge--api'"
            :title="w.source === 'csv'
              ? 'Tydzień zamknięty — CSV z portalu Bolta'
              : 'Bieżący tydzień / brak finalnego importu'"
          >
            <v-icon size="13" :color="w.source === 'csv' ? '#6FE89A' : '#64B5F6'">
              {{ w.source === 'csv' ? 'mdi-check' : 'mdi-cloud-sync' }}
            </v-icon>
            T{{ w.weekNumber }} · {{ w.from.slice(5) }}–{{ w.to.slice(5) }}
          </span>
        </div>
      </details>
    </section>

    <!-- Edit-mode palette — ukrytych widgetów do przeciągnięcia -->
    <template v-if="editMode">
      <div class="filter-section mb-3">
        <div class="filter-section-header">
          <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-shape-plus</v-icon></div>
          <div class="filter-title">Ukryte widgety (przeciągnij na siatkę)</div>
        </div>
        <div class="dash-palette-hint">
          <v-icon size="16" color="#FFB74D">mdi-information-outline</v-icon>
          <span>{{ $t('dashboard.editHint') }}</span>
        </div>
        <div class="d-flex flex-wrap" style="gap: 6px;">
          <span
            v-for="w in hiddenWidgets" :key="w.id"
            class="palette-chip"
            draggable
            @dragstart="onPaletteDragStart($event, w.id)"
          >
            <v-icon size="14" color="#5478FF">{{ w.icon }}</v-icon>
            {{ $t(w.titleKey) }}
          </span>
          <span v-if="!hiddenWidgets.length" class="caption" style="color: #6E7691;">
            {{ $t('dashboard.noHiddenWidgets') }}
          </span>
        </div>
      </div>
    </template>

    <div class="dashboard-widgets-head">
      <div>
        <span>Pulpit operacyjny</span>
        <h2>{{ activeDashboardArea.title }}</h2>
        <p>{{ activeDashboardArea.description }}</p>
      </div>
      <div v-if="editMode" class="dashboard-widget-count"><b>{{ visibleWidgetIds.length }}</b><span>w układzie</span></div>
    </div>

    <nav v-if="!editMode" class="dashboard-area-nav" aria-label="Zakres informacji na pulpicie">
      <button
        v-for="area in dashboardAreas"
        :key="area.id"
        type="button"
        class="dashboard-area-tab"
        :class="{ 'dashboard-area-tab--active': dashboardArea === area.id }"
        :aria-pressed="dashboardArea === area.id ? 'true' : 'false'"
        @click="dashboardArea = area.id"
      >
        <v-icon size="18">{{ area.icon }}</v-icon>
        <span>{{ area.title }}</span>
        <b>{{ areaWidgetCount(area.id) }}</b>
      </button>
    </nav>

    <div v-if="!editMode && areaWidgetCount(dashboardArea) === 0" class="dashboard-empty-area">
      <div class="dashboard-empty-area__icon"><v-icon size="24">mdi-view-grid-plus-outline</v-icon></div>
      <div><strong>Brak widgetów w tym widoku</strong><span>Przejdź do pełnego pulpitu albo dodaj widgety w edycji układu.</span></div>
      <button type="button" class="action-pill action-pill--neutral" @click="dashboardArea = 'all'">Pokaż pełny pulpit</button>
    </div>

    <!-- Grid -->
    <v-row
      class="dashboard-grid"
      @dragover.prevent
      @drop="onDropToGrid"
    >
      <v-col
        v-for="(slot, idx) in layout"
        :key="slot.widgetId"
        v-show="editMode || (slot.visible !== false && matchesDashboardArea(slot.widgetId))"
        :cols="sizeCols(slot.size).cols"
        :sm="sizeCols(slot.size).sm"
        :md="sizeCols(slot.size).md"
        :lg="sizeCols(slot.size).lg"
        class="dashboard-slot"
        :class="[
          `dashboard-slot--${widgetType(slot.widgetId)}`,
          `dashboard-slot--tone-${widgetTone(slot.widgetId)}`,
          { 'btp-drag-over': draggedOverIdx === idx, 'is-dragging': draggedIdx === idx },
        ]"
        @dragover.prevent="onOverSlot(idx)"
        @dragleave="draggedOverIdx = null"
        @drop.stop="onDropOnSlot($event, idx)"
      >
        <div
          class="widget-wrapper"
          :class="{
            editing: editMode,
            'widget-wrapper--compact': slot.size === 'sm',
            'widget-wrapper--wide': slot.size === 'lg' || slot.size === 'xl',
          }"
          :draggable="editMode"
          @dragstart="editMode ? onSlotDragStart($event, idx) : null"
          @dragend="editMode ? onSlotDragEnd() : null"
        >
          <div v-if="editMode" class="widget-edit-bar">
            <v-icon small style="cursor: grab;">mdi-drag</v-icon>
            <span class="caption ml-1 font-weight-medium">{{ widgetTitle(slot.widgetId) }}</span>
            <v-spacer />
            <v-select
              :value="slot.size"
              @change="changeSize(idx, $event)"
              :items="sizeOptions"
              dense hide-details solo flat
              style="max-width: 105px;"
              class="mr-1"
            />
            <v-btn icon x-small @click="hideWidget(idx)" :title="$t('dashboard.hideWidget')">
              <v-icon x-small>mdi-eye-off</v-icon>
            </v-btn>
          </div>
          <div class="widget-content" :style="editMode ? 'pointer-events: none; opacity: 0.85;' : ''">
            <component
              :is="componentFor(slot.widgetId)"
              class="dashboard-widget"
              :filters="appliedFilters"
              :edit-mode="editMode"
            />
          </div>
        </div>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
const axios = require('axios');
import { WIDGETS, widgetById, sizeToCols } from '@/components/dashboard-widgets/widgets';
import { invalidateDashboardCache } from '@/components/dashboard-widgets/_widgetMixin';

function toIsoDate(d) {
  // Formatujemy z lokalnych komponentów daty zamiast toISOString() —
  // żeby nie zsuwać dnia w tył o strefę czasową (np. CEST UTC+2 cofało start tygodnia o 1 dzień).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfIsoWeek(date) {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfIsoWeek(date) {
  const start = startOfIsoWeek(date);
  start.setDate(start.getDate() + 6);
  start.setHours(23, 59, 59, 999);
  return start;
}

// Presety zakresów dat dla dashboardu
function rangeThisWeek() {
  return {
    from: toIsoDate(startOfIsoWeek(new Date())),
    to: toIsoDate(endOfIsoWeek(new Date())),
  };
}
function rangePrevWeek() {
  const ref = new Date();
  ref.setDate(ref.getDate() - 7);
  return {
    from: toIsoDate(startOfIsoWeek(ref)),
    to: toIsoDate(endOfIsoWeek(ref)),
  };
}
function rangeThisMonth() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toIsoDate(first), to: toIsoDate(last) };
}
function rangePrevMonth() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: toIsoDate(first), to: toIsoDate(last) };
}

// Default = poprzedni tydzień (Bolt cron pobiera dane za pn-niedz poprzedni tydzień)
function defaultRange() {
  return rangePrevWeek();
}

const DASHBOARD_AREAS = [
  {
    id: 'overview',
    title: 'Najważniejsze teraz',
    description: 'Skrócony widok wyniku firmy, zobowiązań i spraw wymagających reakcji.',
    icon: 'mdi-view-dashboard-variant-outline',
  },
  {
    id: 'finance',
    title: 'Finanse',
    description: 'Przychody, wypłaty, podatki i pełne rozbicie wyniku firmy.',
    icon: 'mdi-finance',
  },
  {
    id: 'operations',
    title: 'Operacje',
    description: 'Kierowcy, BLIK, oddziały i bieżąca praca zespołu.',
    icon: 'mdi-taxi',
  },
  {
    id: 'analytics',
    title: 'Analizy',
    description: 'Trendy, porównania platform oraz szczegółowe zestawienia.',
    icon: 'mdi-chart-box-outline',
  },
  {
    id: 'all',
    title: 'Pełny pulpit',
    description: 'Wszystkie widgety w zapisanej przez Ciebie kolejności.',
    icon: 'mdi-view-grid-outline',
  },
];

const DASHBOARD_AREA_WIDGETS = {
  overview: new Set([
    'kpi-total-incomes', 'kpi-to-pay', 'kpi-pending-invoices', 'drivers-total',
    'payouts-period', 'blik-pending', 'finance-breakdown', 'revenue-breakdown', 'revenue-chart',
    'earnings-by-platform-pie', 'app-earnings-summary', 'integrations-status',
  ]),
  finance: new Set([
    'revenue-breakdown', 'cost-breakdown', 'finance-breakdown', 'payouts-period', 'kpi-total-incomes', 'kpi-rental-income',
    'kpi-vat8', 'kpi-one-percent', 'kpi-zus-amount', 'kpi-paid', 'kpi-to-pay', 'platform-commissions',
    'app-earnings-summary',
    'kpi-monthly-pending', 'kpi-published', 'company-fees', 'month-summary',
    'table-payouts-by-schedule', 'table-invoices-by-vat', 'table-income-summary',
    'recent-settlements', 'recent-invoices',
  ]),
  operations: new Set([
    'drivers-total', 'drivers-earnings', 'drivers-cash', 'blik-today', 'blik-pending',
    'blik-week', 'orders-by-branch', 'earnings-by-subcompany', 'integrations-status',
    'api-live-earnings', 'table-drivers-by-coop', 'table-drivers-by-zus',
    'table-drivers-by-schedule',
  ]),
  analytics: new Set([
    'revenue-chart', 'earnings-by-platform-pie', 'platform-commissions', 'app-earnings-summary', 'earnings-by-subcompany',
    'orders-by-branch', 'api-live-earnings', 'table-payouts-by-schedule',
    'table-invoices-by-vat', 'table-income-summary', 'table-drivers-by-coop',
    'table-drivers-by-zus', 'table-drivers-by-schedule', 'recent-settlements',
    'recent-invoices',
  ]),
};

const METRIC_WIDGETS = new Set([
  'drivers-total', 'drivers-earnings', 'drivers-cash', 'blik-today', 'blik-pending',
  'blik-week', 'company-fees', 'kpi-pending-invoices', 'kpi-zus-amount',
  'kpi-rental-income', 'kpi-vat8', 'kpi-one-percent', 'kpi-total-incomes',
  'kpi-paid', 'kpi-to-pay', 'kpi-monthly-pending', 'kpi-published',
]);

const WIDGET_TONES = {
  'kpi-total-incomes': 'green',
  'kpi-paid': 'green',
  'kpi-rental-income': 'green',
  'drivers-earnings': 'green',
  'company-fees': 'green',
  'kpi-to-pay': 'orange',
  'kpi-pending-invoices': 'orange',
  'kpi-monthly-pending': 'orange',
  'blik-pending': 'orange',
  'drivers-cash': 'teal',
  'blik-today': 'teal',
  'blik-week': 'teal',
  'integrations-status': 'teal',
  'api-live-earnings': 'teal',
  'finance-breakdown': 'purple',
  'revenue-breakdown': 'green',
  'cost-breakdown': 'orange',
  'platform-commissions': 'orange',
  'kpi-zus-amount': 'purple',
  'kpi-vat8': 'purple',
  'kpi-one-percent': 'purple',
};

export default {
  name: 'DashboardPage',
  data() {
    return {
      layout: [],
      editMode: false,
      filters: { ...defaultRange(), subCompanyIds: [] },
      appliedFilters: { ...defaultRange(), subCompanyIds: [] },
      activePreset: 'prevWeek',
      subCompanies: [],
      draggedIdx: null,
      draggedFromPalette: null,
      draggedOverIdx: null,
      snackbar: { show: false, color: 'success', text: '' },
      boltFetcherStatus: null,
      boltStatusTimer: null,
      sourceStatus: null,
      sourceStatusLoaded: false,
      dashboardArea: 'overview',
      dashboardAreaBeforeEdit: 'overview',
    };
  },
  watch: {
    'filters.from'() { this.scheduleApply(); },
    'filters.to'() { this.scheduleApply(); },
  },
  computed: {
    allWidgetIds() { return WIDGETS.map((w) => w.id); },
    visibleWidgetIds() { return this.layout.filter((s) => s.visible !== false).map((s) => s.widgetId); },
    dashboardAreas() { return DASHBOARD_AREAS; },
    activeDashboardArea() {
      return DASHBOARD_AREAS.find((area) => area.id === this.dashboardArea) || DASHBOARD_AREAS[0];
    },
    hiddenWidgets() {
      const usedIds = new Set(this.layout.map((s) => s.widgetId));
      return WIDGETS.filter((w) => !usedIds.has(w.id));
    },
    sizeOptions() {
      return [
        { text: 'Mały', value: 'sm' },
        { text: 'Średni', value: 'md' },
        { text: 'Szeroki', value: 'lg' },
        { text: 'Pełny', value: 'xl' },
      ];
    },
    sourceIcon() {
      if (!this.sourceStatus) return this.sourceStatusLoaded ? 'mdi-alert-circle-outline' : 'mdi-progress-clock';
      const s = this.sourceStatus?.overallSource;
      if (s === 'csv') return 'mdi-check-circle';
      if (s === 'mixed') return 'mdi-shuffle-variant';
      return 'mdi-cloud-sync';
    },
    sourceIconColor() {
      if (!this.sourceStatus && this.sourceStatusLoaded) return 'warning';
      const s = this.sourceStatus?.overallSource;
      if (s === 'csv') return 'success';
      if (s === 'mixed') return 'warning';
      return 'info';
    },
    sourceSummary() {
      if (!this.sourceStatus) return this.sourceStatusLoaded
        ? 'Nie udało się sprawdzić aktualności danych'
        : 'Sprawdzanie aktualności danych…';
      const s = this.sourceStatus?.overallSource;
      if (s === 'csv') return 'Wszystkie tygodnie zamknięte (CSV)';
      if (s === 'mixed') return 'Mieszanka: zamknięte z CSV, bieżące z API';
      return 'Wszystkie tygodnie z live API';
    },
    sourceShortStatus() {
      if (!this.sourceStatus) return this.sourceStatusLoaded ? 'Brak statusu' : 'Sprawdzanie';
      const s = this.sourceStatus.overallSource;
      if (s === 'csv') return 'Dane zamknięte';
      if (s === 'mixed') return 'Częściowo bieżące';
      return 'Dane bieżące';
    },
    sourceHealthTone() {
      if (!this.sourceStatus) return this.sourceStatusLoaded ? 'warning' : 'loading';
      if (this.sourceStatus.overallSource === 'csv') return 'success';
      if (this.sourceStatus.overallSource === 'mixed') return 'warning';
      return 'info';
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 5) return 'Dobranoc';
      if (h < 12) return 'Dzień dobry';
      if (h < 18) return 'Witaj ponownie';
      return 'Dobry wieczór';
    },
    presetButtons() {
      return [
        { value: 'prevWeek', text: 'Poprzedni tydzień' },
        { value: 'thisWeek', text: 'Ten tydzień' },
        { value: 'prevMonth', text: 'Poprzedni miesiąc' },
        { value: 'thisMonth', text: 'Ten miesiąc' },
        { value: 'custom', text: 'Własny zakres' },
      ];
    },
    // Banner alertu o statusie BoltFetcher — pokazuje się gdy ostatnia synchronizacja
    // FAILED albo > 4h temu. Null = wszystko OK.
    boltStatusBanner() {
      const s = this.boltFetcherStatus;
      if (!s) return null;
      // RUNNING — nie pokazuj alertu
      if (s.running) return null;
      const lastDone = s.lastDone;
      // Brak ostatniego DONE
      if (!lastDone) {
        return {
          type: 'warning',
          title: 'Brak synchronizacji z Boltem',
          message: 'Wejdź w "Synchronizacja kierowców" żeby uruchomić pierwszy fetch.',
        };
      }
      // Ostatni był FAILED
      if (lastDone.status === 'FAILED' || lastDone.error) {
        return {
          type: 'error',
          title: 'Synchronizacja Bolt nie powiodła się',
          message: (lastDone.error || 'Nieznany błąd') + ' — kliknij Sprawdź żeby zobaczyć szczegóły.',
        };
      }
      // Stary fetch (> 4h)
      const ageH = (Date.now() - new Date(lastDone.finishedAt).getTime()) / 3600000;
      if (ageH > 4) {
        return {
          type: 'warning',
          title: 'Dane Bolta są przestarzałe',
          message: `Ostatnia synchronizacja sprzed ${Math.floor(ageH)}h. Cron powinien chodzić co godzinę — sprawdź status.`,
        };
      }
      return null;
    },
  },
  async mounted() {
    // Najpierw poznaj admina (żeby pre-fillować filtr per oddział),
    // potem layout — widgety odpalą się od razu z poprawnym zakresem.
    await this.loadCurrentAdmin();
    await Promise.all([this.loadLayout(), this.loadSubCompanies(), this.loadBoltStatus(), this.loadSourceStatus()]);
    // Refresh Bolt status co 60s — pomiń gdy karta w tle.
    this.boltStatusTimer = setInterval(() => { if (!document.hidden) this.loadBoltStatus(); }, 60000);
  },
  beforeDestroy() {
    if (this.boltStatusTimer) clearInterval(this.boltStatusTimer);
  },
  methods: {
    authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('token') }; },
    apiUrl(p) { return process.env.VUE_APP_ROOT_API + p; },
    async loadBoltStatus() {
      try {
        const res = await axios.get(this.apiUrl('bolt/fetcher/status'), { headers: this.authHeaders() });
        this.boltFetcherStatus = res.data || null;
      } catch (e) {
        // best-effort
      }
    },
    async loadCurrentAdmin() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('company-admins/me'), headers: this.authHeaders() });
        const me = res.data?.data;
        if (!me) return;
        // Head admin / superadmin / hasAccessToAllCompanies — domyślnie WSZYSTKIE oddziały
        const role = me.role || '';
        const actualRole = me.actualRole || role;
        const isPrivileged =
          role === 'superadmin' ||
          actualRole === 'headadmin' ||
          me.isHead === true ||
          me.hasAccessToAllCompanies === true;
        if (isPrivileged) return; // pusta lista subCompanyIds = wszystkie
        // Zwykły admin → pre-fill jego subcompanies
        const ids = (me.subcompanies || []).map(s => s.subcompanyId).filter(Boolean);
        if (ids.length > 0) {
          this.filters.subCompanyIds = ids;
          this.appliedFilters = { ...this.filters };
        }
      } catch (e) {
        // ignore — filtr zostanie pusty (= wszystkie)
      }
    },
    async loadSubCompanies() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('sub-company'), headers: this.authHeaders() });
        const list = res.data?.data?.result || res.data?.data || [];
        this.subCompanies = (Array.isArray(list) ? list : []).map(s => ({ id: s.id, name: s.name || s.city || '—' }));
      } catch (e) {
        // ignore — filtr po prostu zostanie pusty
        this.subCompanies = [];
      }
    },
    async loadLayout() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('dashboard/config'), headers: this.authHeaders() });
        const layout = (res.data && res.data.data && res.data.data.layout) || [];
        // Filter to only widgets known in registry (in case widget was removed)
        this.layout = layout.filter((s) => WIDGETS.some((w) => w.id === s.widgetId));
      } catch (e) {
        // Fall back to all widgets default
        this.layout = WIDGETS.map((w) => ({ widgetId: w.id, size: w.defaultSize, visible: true }));
      }
    },
    componentFor(id) {
      const w = widgetById(id);
      return w ? w.component : null;
    },
    widgetTitle(id) {
      const w = widgetById(id);
      return w ? this.$t(w.titleKey) : id;
    },
    sizeCols(size) { return sizeToCols(size); },
    matchesDashboardArea(widgetId, areaId = this.dashboardArea) {
      if (areaId === 'all') return true;
      const widgets = DASHBOARD_AREA_WIDGETS[areaId];
      return widgets ? widgets.has(widgetId) : true;
    },
    areaWidgetCount(areaId) {
      return this.layout.filter((slot) => slot.visible !== false && this.matchesDashboardArea(slot.widgetId, areaId)).length;
    },
    widgetType(id) {
      if (METRIC_WIDGETS.has(id)) return 'metric';
      if (id === 'revenue-chart' || id === 'earnings-by-platform-pie') return 'chart';
      if (id.startsWith('table-') || id.startsWith('recent-') || id === 'orders-by-branch' || id === 'earnings-by-subcompany') return 'table';
      return 'feature';
    },
    widgetTone(id) { return WIDGET_TONES[id] || 'blue'; },
    applyFilters() {
      invalidateDashboardCache();
      this.appliedFilters = { ...this.filters };
      this.loadSourceStatus();
    },
    async loadSourceStatus() {
      if (!this.appliedFilters.from || !this.appliedFilters.to) return;
      this.sourceStatusLoaded = false;
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('dashboard/source-status'),
          params: { from: this.appliedFilters.from, to: this.appliedFilters.to },
          headers: this.authHeaders(),
        });
        this.sourceStatus = res.data?.data || null;
      } catch (e) {
        this.sourceStatus = null;
      } finally {
        this.sourceStatusLoaded = true;
      }
    },
    onPresetChange(val) {
      const preset = val || this.activePreset;
      // Mapuj preset na zakres dat
      const map = {
        prevWeek: rangePrevWeek,
        thisWeek: rangeThisWeek,
        prevMonth: rangePrevMonth,
        thisMonth: rangeThisMonth,
      };
      const fn = map[preset];
      if (fn) {
        const r = fn();
        this.filters = { ...this.filters, ...r };
        this.applyFilters();
      }
      // 'custom' — nic nie robimy, user sam ustawi daty
    },
    formatRange(from, to) {
      if (!from || !to) return '—';
      const fmt = (iso) => {
        const [y, m, d] = iso.split('-');
        return `${d}.${m}.${y}`;
      };
      return `${fmt(from)} — ${fmt(to)}`;
    },
    scheduleApply() {
      if (!this.filters.from || !this.filters.to) return;
      if (this._applyTimer) clearTimeout(this._applyTimer);
      this._applyTimer = setTimeout(() => { this.applyFilters(); }, 300);
    },
    async toggleEdit() {
      if (!this.editMode) {
        this.dashboardAreaBeforeEdit = this.dashboardArea;
        this.dashboardArea = 'all';
        this.editMode = true;
        return;
      }
      const saved = await this.saveLayout();
      if (saved) {
        this.dashboardArea = this.dashboardAreaBeforeEdit;
        this.editMode = false;
      }
    },
    async saveLayout() {
      try {
        await axios({
          method: 'put',
          url: this.apiUrl('dashboard/config'),
          headers: this.authHeaders(),
          data: { layout: this.layout, scope: 'admin' },
        });
        this.snackbar = { show: true, color: 'success', text: this.$t('dashboard.layoutSaved') };
        return true;
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || 'Error';
        const text = Array.isArray(msg) ? msg.join(', ') : String(msg);
        console.error('saveLayout error:', e.response ? e.response.data : e);
        this.snackbar = { show: true, color: 'error', text };
        return false;
      }
    },
    async resetLayout() {
      if (!confirm(this.$t('dashboard.confirmReset'))) return;
      try {
        await axios({
          method: 'delete',
          url: this.apiUrl('dashboard/config?scope=admin'),
          headers: this.authHeaders(),
        });
        await this.loadLayout();
        this.snackbar = { show: true, color: 'success', text: this.$t('dashboard.layoutReset') };
      } catch (e) { /* ignore */ }
    },
    changeSize(idx, size) {
      this.$set(this.layout[idx], 'size', size);
    },
    hideWidget(idx) {
      this.layout.splice(idx, 1);
    },
    onPaletteDragStart(e, widgetId) {
      this.draggedFromPalette = widgetId;
      this.draggedIdx = null;
      e.dataTransfer.effectAllowed = 'move';
    },
    onSlotDragStart(e, idx) {
      this.draggedIdx = idx;
      this.draggedFromPalette = null;
      e.dataTransfer.effectAllowed = 'move';
    },
    onSlotDragEnd() {
      this.draggedIdx = null;
      this.draggedOverIdx = null;
    },
    onOverSlot(idx) { this.draggedOverIdx = idx; },
    onDropOnSlot(e, targetIdx) {
      e.preventDefault();
      this.draggedOverIdx = null;
      if (this.draggedFromPalette) {
        const widget = widgetById(this.draggedFromPalette);
        if (widget) {
          this.layout.splice(targetIdx, 0, { widgetId: widget.id, size: widget.defaultSize, visible: true });
        }
        this.draggedFromPalette = null;
      } else if (this.draggedIdx !== null && this.draggedIdx !== targetIdx) {
        const [moved] = this.layout.splice(this.draggedIdx, 1);
        this.layout.splice(targetIdx, 0, moved);
      }
      this.draggedIdx = null;
    },
    onDropToGrid() {
      if (this.draggedFromPalette) {
        const widget = widgetById(this.draggedFromPalette);
        if (widget) {
          this.layout.push({ widgetId: widget.id, size: widget.defaultSize, visible: true });
        }
        this.draggedFromPalette = null;
      }
    },
  },
};
</script>

<style scoped>
.admin-dashboard {
  --dash-bg: #171B26;
  --dash-surface: #232838;
  --dash-surface-soft: #1F2434;
  --dash-surface-hover: #292F40;
  --dash-border: rgba(255, 255, 255, 0.075);
  --dash-border-strong: rgba(126, 151, 255, 0.22);
  --dash-text: #F5F7FC;
  --dash-muted: #AEB8CA;
  --dash-subtle: #8B96AA;
  --dash-blue: #5B7CFF;
  --dash-green: #42D787;
  --dash-orange: #FFB74D;
  --dash-red: #FF7373;
  --dash-purple: #A78BFA;
  min-height: calc(100vh - 56px);
  margin: -24px;
  padding: 24px clamp(16px, 2.2vw, 34px) 52px;
  overflow: hidden;
  background:
    radial-gradient(circle at 7% -8%, rgba(91, 124, 255, 0.16), transparent 28%),
    radial-gradient(circle at 93% 7%, rgba(167, 139, 250, 0.09), transparent 25%),
    var(--dash-bg);
  color: var(--dash-text);
}
.admin-dashboard * { box-sizing: border-box; }
.page-header,
.bolt-banner,
.dashboard-control,
.filter-section,
.dashboard-widgets-head,
.dashboard-area-nav,
.dashboard-grid {
  max-width: 1680px;
  margin-right: auto !important;
  margin-left: auto !important;
}

/* Nagłówek właścicielski */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 2px 2px 20px;
}
.page-header-main { display: flex; min-width: 0; align-items: center; gap: 14px; }
.page-header-text { min-width: 0; }
.page-header-icon {
  display: flex;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(142, 167, 255, 0.28);
  border-radius: 17px;
  background: linear-gradient(145deg, #4168FF, #755EF0);
  box-shadow: 0 18px 34px -18px rgba(80, 107, 255, 0.88);
}
.page-eyebrow {
  color: #91A0B7;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}
.page-title {
  margin: 3px 0 0;
  color: #FFFFFF;
  font-size: clamp(25px, 2.5vw, 32px);
  font-weight: 900;
  letter-spacing: -0.045em;
  line-height: 1.05;
}
.page-subtitle { max-width: 700px; margin: 7px 0 0; color: var(--dash-muted); font-size: 13px; line-height: 1.45; }
.page-header-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 9px; }
.dashboard-health {
  display: flex;
  min-width: 150px;
  align-items: center;
  gap: 9px;
  padding: 8px 11px;
  border: 1px solid var(--dash-border);
  border-radius: 13px;
  background: rgba(35, 40, 56, 0.72);
}
.dashboard-health__dot {
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: #64B5F6;
  box-shadow: 0 0 0 4px rgba(100, 181, 246, 0.1);
}
.dashboard-health--success .dashboard-health__dot { background: var(--dash-green); box-shadow: 0 0 0 4px rgba(66, 215, 135, 0.11); }
.dashboard-health--warning .dashboard-health__dot { background: var(--dash-orange); box-shadow: 0 0 0 4px rgba(255, 183, 77, 0.11); }
.dashboard-health--loading .dashboard-health__dot { animation: dashboard-pulse 1.4s ease-in-out infinite; }
.dashboard-health small { display: block; color: var(--dash-subtle); font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.dashboard-health strong { display: block; margin-top: 1px; color: #DDE3EE; font-size: 11.5px; line-height: 1.2; }

/* Wspólne akcje */
.action-pill {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.055);
  color: #E8ECF4;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
}
.action-pill:not(.action-pill--primary):not(.action-pill--warning):not(.action-pill--error):hover { border-color: rgba(255, 255, 255, 0.2); background: rgba(255, 255, 255, 0.085); }
.action-pill:focus-visible,
.hero-preset:focus-visible,
.dashboard-area-tab:focus-visible { outline: 2px solid #8EA7FF; outline-offset: 2px; }
.action-pill .v-icon { color: inherit !important; }
.action-pill--primary { border-color: #5B7CFF; background: #5B7CFF; color: #FFFFFF; box-shadow: 0 10px 24px -15px rgba(91, 124, 255, 0.95); }
.action-pill--primary:hover { border-color: #6D89FF; background: #6D89FF; }
.action-pill--neutral { color: #C2CAD8; }
.action-pill--warning { border-color: rgba(255, 183, 77, 0.32); background: rgba(255, 183, 77, 0.12); color: #FFD08A; }
.action-pill--error { border-color: rgba(255, 115, 115, 0.3); background: rgba(255, 115, 115, 0.11); color: #FF9B9B; }

/* Alert synchronizacji */
.bolt-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px !important;
  padding: 13px 15px;
  border-radius: 15px;
}
.bolt-banner--error { border: 1px solid rgba(255, 115, 115, 0.28); background: rgba(255, 115, 115, 0.09); }
.bolt-banner--warning { border: 1px solid rgba(255, 183, 77, 0.25); background: rgba(255, 183, 77, 0.08); }
.bolt-banner-title { color: #F6F8FC; font-size: 13px; font-weight: 900; }
.bolt-banner-msg {
  display: -webkit-box;
  margin-top: 2px;
  overflow: hidden;
  color: #B5BFCE;
  font-size: 11.5px;
  line-height: 1.4;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

/* Centrum sterowania */
.dashboard-control {
  margin-bottom: 14px !important;
  padding: 18px;
  border: 1px solid var(--dash-border);
  border-radius: 20px;
  background:
    radial-gradient(circle at 84% -20%, rgba(91, 124, 255, 0.13), transparent 42%),
    rgba(35, 40, 56, 0.96);
  box-shadow: 0 28px 55px -47px rgba(0, 0, 0, 0.95);
}
.dashboard-control-top { display: flex; align-items: stretch; justify-content: space-between; gap: 16px; }
.dash-period-block { display: flex; min-width: 0; align-items: center; gap: 12px; }
.dash-control-icon,
.dash-source-icon {
  display: flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
  background: rgba(91, 124, 255, 0.13);
}
.dash-control-icon >>> .v-icon { color: #7896FF !important; }
.dash-hero-label { margin-bottom: 3px; color: #91A0B7; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.dash-hero-range { color: #FFFFFF; font-size: clamp(21px, 2.2vw, 28px); font-weight: 900; letter-spacing: -0.04em; line-height: 1.05; font-variant-numeric: tabular-nums; }
.dash-source-summary {
  display: flex;
  min-width: 290px;
  max-width: 390px;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 14px;
  background: rgba(23, 27, 38, 0.56);
}
.dash-source-summary small { display: block; color: #8F9BAF; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.dash-source-summary strong { display: block; margin-top: 2px; color: #D6DCE7; font-size: 11.5px; line-height: 1.35; }
.dash-source-summary.source-csv .dash-source-icon { background: rgba(66, 215, 135, 0.12); }
.dash-source-summary.source-mixed .dash-source-icon { background: rgba(255, 183, 77, 0.12); }
.dash-hero-presets { display: flex; gap: 6px; margin-top: 14px; overflow-x: auto; padding-bottom: 2px; scrollbar-width: thin; }
.hero-preset {
  min-height: 36px;
  flex: 0 0 auto;
  padding: 7px 13px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 10px;
  background: #1B202E;
  color: #AEB8CA;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
}
.hero-preset:not(.hero-preset--active):hover { border-color: rgba(142, 167, 255, 0.24); color: #FFFFFF; }
.hero-preset--active { border-color: #5B7CFF; background: #5B7CFF; color: #FFFFFF; box-shadow: 0 9px 22px -14px rgba(91, 124, 255, 0.9); }
.dashboard-filter-row {
  display: grid;
  grid-template-columns: auto minmax(250px, 430px) repeat(2, minmax(150px, 190px)) auto;
  align-items: center;
  gap: 9px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.055);
}
.dashboard-filter-label { display: flex; align-items: center; gap: 6px; padding-right: 4px; color: #AEB8CA; font-size: 11.5px; font-weight: 700; white-space: nowrap; }
.dashboard-filter-label >>> .v-icon { color: #7896FF !important; }
.dashboard-branch-filter,
.dashboard-date-filter { min-width: 0; }
.dash-more-selection { color: #9AA6BA !important; }
.dashboard-refresh { align-self: stretch; white-space: nowrap; }
.dashboard-control >>> .v-text-field--outlined .v-input__slot,
.dashboard-control >>> .v-select--outlined .v-input__slot {
  min-height: 42px !important;
  border-radius: 11px !important;
  background: #191E2B !important;
}
.dashboard-control >>> fieldset { border-color: rgba(255, 255, 255, 0.1) !important; }
.dashboard-control >>> .v-input--is-focused fieldset { border-color: #6F8BFF !important; }
.dashboard-control >>> .v-label { color: #98A4B8 !important; font-size: 12px !important; }
.dashboard-control >>> input,
.dashboard-control >>> .v-select__selection { color: #F5F7FC !important; font-size: 12px; }
.dashboard-control >>> .v-input__icon .v-icon { color: #8490A5 !important; }
.dashboard-control >>> .theme--dark.v-chip { border-radius: 999px !important; background: rgba(91, 124, 255, 0.13) !important; color: #C3CEFF !important; }
.dashboard-source-details {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}
.dashboard-source-details summary {
  display: flex;
  min-height: 30px;
  align-items: center;
  gap: 10px;
  color: #8F9BAF;
  font-size: 10.5px;
  cursor: pointer;
  list-style: none;
}
.dashboard-source-details summary::-webkit-details-marker { display: none; }
.dashboard-source-row-label { display: inline-flex; align-items: center; gap: 6px; color: #B8C2D1; font-size: 11px; font-weight: 700; }
.dashboard-source-row-label >>> .v-icon { color: #7587C8 !important; }
.dashboard-source-chevron { margin-left: auto; color: #7E899E !important; transition: transform 0.16s ease; }
.dashboard-source-details[open] .dashboard-source-chevron { transform: rotate(180deg); }
.dash-source-strip { display: flex; gap: 6px; margin-top: 8px; overflow-x: auto; padding-bottom: 2px; scrollbar-width: thin; }
.src-badge { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 4px; padding: 5px 10px; border-radius: 999px; font-size: 10.5px; font-weight: 700; white-space: nowrap; }
.src-badge--csv { background: rgba(66, 215, 135, 0.12); color: #7BECA5; }
.src-badge--api { background: rgba(100, 181, 246, 0.12); color: #86C8FA; }

/* Tryb edycji */
.filter-section {
  margin-bottom: 14px !important;
  padding: 15px 16px;
  border: 1px solid rgba(91, 124, 255, 0.16);
  border-radius: 17px;
  background: rgba(35, 40, 56, 0.93);
}
.filter-section-header { display: flex; align-items: center; gap: 9px; margin-bottom: 10px; }
.filter-icon { display: flex; width: 34px; height: 34px; align-items: center; justify-content: center; border-radius: 11px; background: rgba(91, 124, 255, 0.13); }
.filter-title { color: #F5F7FC; font-size: 13px; font-weight: 900; }
.dash-palette-hint { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; padding: 9px 10px; border: 1px solid rgba(255, 183, 77, 0.2); border-radius: 11px; background: rgba(255, 183, 77, 0.08); color: #FFD18B; font-size: 11.5px; }
.palette-chip { display: inline-flex; align-items: center; gap: 6px; padding: 7px 9px; border: 1px solid var(--dash-border); border-radius: 9px; background: #191E2B; color: #D0D6E1; font-size: 11px; font-weight: 700; cursor: grab; }

/* Nawigacja po obszarach */
.dashboard-widgets-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  padding: 19px 4px 10px;
}
.dashboard-widgets-head > div:first-child > span { color: #8795AA; font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; }
.dashboard-widgets-head h2 { margin: 3px 0 0; color: #FFFFFF; font-size: 20px; font-weight: 900; letter-spacing: -0.035em; }
.dashboard-widgets-head p { max-width: 760px; margin: 5px 0 0; color: #9AA6B9; font-size: 12px; line-height: 1.45; }
.dashboard-widget-count { display: flex; flex-direction: column; align-items: flex-end; }
.dashboard-widget-count b { color: #FFFFFF; font-size: 20px; font-weight: 900; line-height: 1; }
.dashboard-widget-count span { margin-top: 3px; color: #8894A9; font-size: 9.5px; font-weight: 700; text-transform: uppercase; }
.dashboard-area-nav {
  display: flex;
  gap: 6px;
  margin-bottom: 8px !important;
  padding: 5px;
  overflow-x: auto;
  border: 1px solid var(--dash-border);
  border-radius: 15px;
  background: rgba(31, 36, 52, 0.82);
  scrollbar-width: thin;
}
.dashboard-area-tab {
  display: inline-flex;
  min-height: 40px;
  flex: 0 0 auto;
  align-items: center;
  gap: 7px;
  padding: 8px 11px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: #AAB4C5;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
}
.dashboard-area-tab .v-icon { color: #7F8AA0 !important; }
.dashboard-area-tab b { min-width: 21px; padding: 2px 6px; border-radius: 999px; background: rgba(255, 255, 255, 0.055); color: #9DA9BB; font-size: 10.5px; text-align: center; }
.dashboard-area-tab:not(.dashboard-area-tab--active):hover { background: rgba(255, 255, 255, 0.035); color: #FFFFFF; }
.dashboard-area-tab--active { border-color: rgba(126, 151, 255, 0.22); background: rgba(91, 124, 255, 0.14); color: #FFFFFF; }
.dashboard-area-tab--active .v-icon { color: #7896FF !important; }
.dashboard-area-tab--active b { background: rgba(91, 124, 255, 0.2); color: #B9C6FF; }
.dashboard-empty-area {
  display: flex;
  max-width: 1680px;
  min-height: 112px;
  align-items: center;
  gap: 13px;
  margin: 12px auto 4px;
  padding: 18px;
  border: 1px dashed rgba(126, 151, 255, 0.22);
  border-radius: 16px;
  background: rgba(35, 40, 56, 0.6);
}
.dashboard-empty-area__icon { display: flex; width: 44px; height: 44px; flex: 0 0 44px; align-items: center; justify-content: center; border-radius: 14px; background: rgba(91, 124, 255, 0.12); }
.dashboard-empty-area__icon >>> .v-icon { color: #7896FF !important; }
.dashboard-empty-area > div:nth-child(2) { min-width: 0; }
.dashboard-empty-area strong { display: block; color: #F5F7FC; font-size: 13px; }
.dashboard-empty-area span { display: block; margin-top: 3px; color: #98A4B8; font-size: 11.5px; }
.dashboard-empty-area .action-pill { margin-left: auto; }

/* Siatka i shell widgetów */
.dashboard-grid { margin: 0 auto !important; }
.dashboard-grid > .dashboard-slot { padding: 6px; }
.dashboard-slot { --slot-accent: #6F8BFF; min-width: 0; transition: opacity 0.16s ease, background 0.16s ease; }
.dashboard-slot--tone-green { --slot-accent: #42D787; }
.dashboard-slot--tone-orange { --slot-accent: #FFB74D; }
.dashboard-slot--tone-teal { --slot-accent: #35D4C0; }
.dashboard-slot--tone-purple { --slot-accent: #A78BFA; }
.widget-wrapper,
.widget-content,
.widget-content > * { min-width: 0; height: 100%; }
.widget-wrapper { position: relative; }
.widget-content >>> .dashboard-widget.v-card {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--dash-border) !important;
  border-radius: 19px !important;
  background: var(--dash-surface) !important;
  color: var(--dash-text) !important;
  box-shadow: 0 20px 44px -38px rgba(0, 0, 0, 0.95) !important;
}
.dashboard-slot--feature .widget-content >>> .dashboard-widget.v-card::before,
.dashboard-slot--chart .widget-content >>> .dashboard-widget.v-card::before,
.dashboard-slot--table .widget-content >>> .dashboard-widget.v-card::before {
  position: absolute;
  top: 0;
  right: 18px;
  left: 18px;
  z-index: 1;
  height: 2px;
  border-radius: 0 0 3px 3px;
  background: linear-gradient(90deg, transparent, var(--slot-accent), transparent);
  opacity: 0.55;
  content: '';
}
.widget-content >>> .dashboard-widget.v-card > .v-card__title {
  min-height: 60px;
  padding: 15px 16px 11px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  color: #F3F6FB !important;
  font-size: 14px !important;
  font-weight: 900 !important;
  letter-spacing: -0.015em !important;
}
.widget-content >>> .dashboard-widget.v-card > .v-card__title .v-icon { color: var(--slot-accent) !important; }
.widget-content >>> .dashboard-widget.v-card > .v-card__subtitle { padding: 8px 16px 0 !important; color: #8F9BAF !important; font-size: 11.5px !important; line-height: 1.4; }
.widget-content >>> .dashboard-widget.v-card > .v-card__text { padding: 14px 16px 16px !important; color: #C0C8D6 !important; }
.widget-content >>> .dashboard-widget.v-card .v-card {
  border: 1px solid rgba(255, 255, 255, 0.055) !important;
  border-radius: 13px !important;
  background: var(--dash-surface-soft) !important;
  box-shadow: none !important;
}
.widget-content >>> .text-h4,
.widget-content >>> .text-h5,
.widget-content >>> .text-h6,
.widget-content >>> .display-1,
.widget-content >>> .font-weight-bold { font-variant-numeric: tabular-nums; }
.widget-content >>> .grey--text,
.widget-content >>> .text--secondary { color: #94A0B4 !important; }
.widget-content >>> .caption { font-size: 11.5px !important; line-height: 1.4; }
.widget-content >>> .success--text { color: #66E89C !important; }
.widget-content >>> .warning--text,
.widget-content >>> .orange--text { color: #FFC36B !important; }
.widget-content >>> .error--text,
.widget-content >>> .red--text { color: #FF8B8B !important; }

/* Tabele */
.widget-content >>> .dashboard-widget.v-card > .v-data-table,
.widget-content >>> .dashboard-widget.v-card > .v-simple-table { border: 0 !important; border-radius: 0 !important; background: transparent !important; }
.widget-content >>> .v-data-table__wrapper,
.widget-content >>> .v-simple-table__wrapper { overflow-x: auto; }
.widget-content >>> table { min-width: 100%; color: #E8ECF3; }
.widget-content >>> thead tr th {
  height: 42px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.065) !important;
  background: #1D2231 !important;
  color: #98A4B8 !important;
  font-size: 11.5px !important;
  font-weight: 700 !important;
  letter-spacing: 0.045em;
  text-transform: uppercase;
  white-space: nowrap;
}
.widget-content >>> tbody tr { background: transparent !important; }
.widget-content >>> tbody tr:hover { background: rgba(255, 255, 255, 0.025) !important; }
.widget-content >>> tbody tr.green.lighten-5,
.widget-content >>> tbody tr.blue-grey.lighten-5 { background: rgba(66, 215, 135, 0.07) !important; }
.widget-content >>> tbody td {
  height: 47px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
  color: #DDE3ED !important;
  font-size: 12px !important;
  font-variant-numeric: tabular-nums;
}
.admin-dashboard .widget-content >>> .theme--dark.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  height: 42px !important;
  padding: 0 12px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.065) !important;
  background: #1D2231 !important;
  color: #A7B2C4 !important;
  font-size: 11.5px !important;
  font-weight: 700 !important;
}
.admin-dashboard .widget-content >>> .theme--dark.v-data-table > .v-data-table__wrapper > table > tbody > tr {
  background: transparent !important;
}
.admin-dashboard .widget-content >>> .theme--dark.v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  background: rgba(255, 255, 255, 0.025) !important;
}
.admin-dashboard .widget-content >>> .theme--dark.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  height: 47px !important;
  padding: 9px 12px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
  color: #DDE3ED !important;
  font-size: 12px !important;
}
.widget-content >>> .v-data-footer { min-height: 46px; border-top: 1px solid rgba(255, 255, 255, 0.05) !important; background: #1D2231 !important; color: #AEB8CA !important; font-size: 11px; }

/* Kontrolki wewnątrz widgetów */
.widget-content >>> .v-btn { min-height: 32px; border-radius: 9px !important; font-weight: 700 !important; letter-spacing: 0 !important; text-transform: none !important; }
.widget-content >>> .v-btn-toggle { overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.085) !important; border-radius: 10px !important; background: #191E2B !important; }
.widget-content >>> .v-btn-toggle .v-btn { border: 0 !important; background: transparent !important; color: #AEB8CA !important; }
.widget-content >>> .v-btn-toggle .v-btn--active { background: #5B7CFF !important; color: #FFFFFF !important; }
.widget-content >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.widget-content >>> .v-divider { border-color: rgba(255, 255, 255, 0.06) !important; }
.widget-content >>> .v-alert { border-radius: 12px !important; font-size: 11.5px; }
.widget-content >>> .v-text-field--outlined .v-input__slot { border-radius: 10px !important; background: #191E2B !important; }
.widget-content >>> .v-text-field input { color: #F5F7FC !important; }

/* Edycja układu */
.widget-wrapper.editing {
  padding: 5px;
  border: 1px dashed rgba(126, 151, 255, 0.48);
  border-radius: 16px;
  background: rgba(91, 124, 255, 0.045);
  cursor: move;
}
.widget-edit-bar { display: flex; min-height: 40px; align-items: center; gap: 5px; margin-bottom: 5px; padding: 6px 8px; border: 1px solid rgba(91, 124, 255, 0.16); border-radius: 10px; background: rgba(91, 124, 255, 0.1); color: #D4DCF0; }
.widget-edit-bar > .caption { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dashboard-slot.btp-drag-over { border-radius: 16px; background: rgba(91, 124, 255, 0.12); }
.dashboard-slot.is-dragging { opacity: 0.42; }

@keyframes dashboard-pulse {
  0%, 100% { opacity: 0.45; transform: scale(0.86); }
  50% { opacity: 1; transform: scale(1); }
}

@media (max-width: 1100px) {
  .dashboard-filter-row { grid-template-columns: auto minmax(220px, 1fr) repeat(2, minmax(140px, 170px)); }
  .dashboard-refresh { grid-column: 2 / -1; justify-self: end; }
}

@media (max-width: 820px) {
  .admin-dashboard { margin: -16px; padding: 18px 12px 42px; }
  .page-header { align-items: flex-start; }
  .page-header-actions { align-items: stretch; flex-direction: column; }
  .dashboard-health { min-width: 180px; }
  .dashboard-control-top { flex-direction: column; }
  .dash-source-summary { width: 100%; max-width: none; min-width: 0; }
  .dashboard-filter-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .dashboard-filter-label,
  .dashboard-branch-filter { grid-column: 1 / -1; }
  .dashboard-refresh { grid-column: 1 / -1; justify-self: stretch; }
}

@media (max-width: 600px) {
  .page-header { flex-direction: column; }
  .page-header-main { align-items: flex-start; }
  .page-header-icon { width: 46px; height: 46px; flex-basis: 46px; border-radius: 15px; }
  .page-header-actions { width: 100%; flex-direction: row; flex-wrap: wrap; }
  .dashboard-health { min-width: 0; flex: 1 1 170px; }
  .page-header-actions .action-pill { flex: 1 1 auto; }
  .bolt-banner { align-items: flex-start; flex-wrap: wrap; }
  .bolt-banner .action-pill { width: 100%; }
  .dashboard-control { padding: 14px 12px; border-radius: 17px; }
  .dash-hero-range { font-size: 21px; }
  .dash-hero-presets { margin-right: -12px; padding-right: 12px; }
  .dashboard-filter-row { grid-template-columns: minmax(0, 1fr); }
  .dashboard-filter-label,
  .dashboard-branch-filter,
  .dashboard-date-filter,
  .dashboard-refresh { grid-column: 1; }
  .dashboard-control >>> input { font-size: 16px !important; }
  .dashboard-source-details summary > span:nth-child(2) { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dashboard-widgets-head { align-items: flex-start; padding-top: 17px; }
  .dashboard-widgets-head h2 { font-size: 19px; }
  .dashboard-area-nav { margin-right: -2px !important; margin-left: -2px !important; }
  .dashboard-empty-area { align-items: flex-start; flex-wrap: wrap; }
  .dashboard-empty-area .action-pill { width: 100%; margin-left: 0; }
  .dashboard-grid { margin: 0 !important; }
  .dashboard-grid > .dashboard-slot { padding: 5px 0; }
  .widget-content >>> .dashboard-widget.v-card > .v-card__title { align-items: flex-start; min-height: 56px; flex-wrap: wrap; }
}

@media (max-width: 430px) {
  .page-title { font-size: 25px; }
  .page-subtitle { font-size: 12px; }
  .dashboard-health { flex-basis: 100%; }
  .dashboard-source-details summary { gap: 7px; }
  .dashboard-source-row-label { flex: 0 0 auto; }
  .dashboard-widgets-head p { font-size: 11.5px; }
}

@media (prefers-reduced-motion: reduce) {
  .action-pill,
  .dashboard-health__dot,
  .dashboard-source-chevron { animation: none !important; transition: none !important; }
}
</style>
